import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Idp } from '@/types';
import { getIdpProvider, IDP_TYPES } from '@/types';
import type { CellContext } from '@tanstack/react-table';
import { Shield } from 'lucide-react';
import { useIdps } from '@/store/idp';
import { Button } from '@/components/ui/Button';
import { useLayout } from '@/contexts/layout-context';
import { Loader } from '@/components/loader';
import { ComposedTable, BadgeCell, ActionCell, type FilterDef, type FilterValues } from '@/components/table';
import { PageLayout } from '@/components/page-layout';

const formatCreatedAt = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
};

const FILTERS: FilterDef[] = [
  { type: 'chips', key: 'types', label: 'Provider / Type', options: [...IDP_TYPES] },
  { type: 'radio', key: 'period', label: 'Created Date', options: ['All Time', '2024 Only', '2023 Only'] },
];

type IdpCell = CellContext<Idp, unknown>;

export const IdpsPage = () => {
  const [idps, setIdps] = useState<Idp[]>([]);
  const [loading, setLoading] = useState(true);
  const listIdps = useIdps((state) => state.list);
  const updateIdp = useIdps((state) => state.update);
  const navigate = useNavigate();
  const { setBreadcrumbs } = useLayout();

  const fetchIdps = async () => {
    setLoading(true);
    try { 
        const fetchedIdps = await listIdps();
        setIdps(fetchedIdps.filter(idp => IDP_TYPES.includes(idp.provider as any)));
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  useEffect(() => {
    setBreadcrumbs([{ name: 'Identity Providers' }]);
    fetchIdps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleEnabled = async (e: React.MouseEvent, id: string, enabled: boolean) => {
    e.stopPropagation();
    try {
      await updateIdp(id, { status: !enabled ? 1 : 0 } as any);
      setIdps(prev => prev.map(i => i.id === id ? { ...i, enabled: !enabled, status: !enabled ? 1 : 0 } : i));
    } catch (err) { console.error(err); }
  };

  const columns = [
    {
      id: 'name',
      header: 'Provider Name',
      accessorKey: 'display_name',
      cell: ({ row }: IdpCell) => {
        const i = row.original as Idp;
        return (
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden border border-slate-200/60 dark:border-slate-700/60 shadow bg-white dark:bg-slate-800 shrink-0">
              {i.logo_url
                ? <img src={i.logo_url} alt={i.display_name} className="w-6 h-6 object-contain" />
                : (getIdpProvider(i.provider)?.logoUrl
                   ? <img src={getIdpProvider(i.provider)!.logoUrl} alt={i.display_name} className="w-6 h-6 object-contain" />
                   : <Shield className="w-5 h-5 text-slate-400" />)}
            </div>
            <div className="flex flex-col">
              <span className="text-[14px] font-bold text-slate-900 dark:text-white cursor-pointer hover:text-[--primary]" onClick={() => navigate(`/m/idps/${i.id}`)}>
                {i.display_name}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }: IdpCell) => {
        const i = row.original as Idp;
        const isActive = i.enabled;
        const cls = isActive
          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20'
          : 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
        const dotCls = isActive ? 'bg-emerald-500' : 'bg-slate-400';
        return (
          <button onClick={(e) => toggleEnabled(e, i.id, i.enabled)}
            className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border flex items-center gap-1.5 w-max hover:opacity-80 transition-opacity ${cls}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${dotCls}`} />
            {isActive ? 'Active' : 'Inactive'}
          </button>
        );
      },
    },
    {
      id: 'provider',
      header: 'Protocol',
      cell: ({ row }: IdpCell) => <BadgeCell label={row.original.provider} />,
    },
    {
      id: 'created',
      header: 'Created Date',
      cell: ({ row }: IdpCell) => <span className="text-[13px] text-slate-500 dark:text-slate-400">{formatCreatedAt(row.original.created_at)}</span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      meta: { align: 'right' as const },
      cell: ({ row }: IdpCell) => <ActionCell onClick={() => navigate(`/m/idps/${row.original.id}`)} />,
    },
  ];

  const filterFn = (row: Idp, values: FilterValues) => {
    const types = values['types'] as string[];
    if (types?.length > 0 && !types.includes(row.provider)) return false;
    const period = values['period'] as string;
    if (period && period !== 'All Time') {
      const date = formatCreatedAt(row.created_at);
      if (period === '2024 Only' && !date.includes('2024')) return false;
      if (period === '2023 Only' && !date.includes('2023')) return false;
    }
    return true;
  };

  const emptyState = (
    <div className="p-12 text-center flex flex-col items-center justify-center bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900">
      <div className="w-16 h-16 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
        <Shield className="w-8 h-8 text-slate-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-900 dark:text-white">No identity providers found</h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-xs">Start by configuring your first external identity source.</p>
      <Button className="mt-6 bg-[#1e293b] hover:bg-slate-800 text-white rounded-full px-6" onClick={() => navigate('/m/idps/create')}>Create first provider</Button>
    </div>
  );

  return (
    <PageLayout title="Identity Providers" description="Configure federated identity providers and SAML/OIDC configurations.">
      {loading ? (
        <div className="flex justify-center py-20 bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-900"><Loader /></div>
      ) : (
        <ComposedTable
          data={idps}
          columns={columns}
          searchPlaceholder="Search providers by name or type..."
          statusOptions={['All', 'Active', 'Inactive']}
          statusField={(i: Idp) => i.enabled ? 'Active' : 'Inactive'}
          filters={FILTERS}
          filterFn={filterFn}
          onAdd={() => navigate('/m/idps/create')}
          emptyState={emptyState}
          itemLabel="providers"
        />
      )}
    </PageLayout>
  );
};